import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { RecallToothStatus, RecallToothStatusDetail, PatientRecall } from '@app/shared/models';
import { Patient } from '@app/core/patients/patient';
import { PrintService } from '@app/shared/services/helpers/print.service';
import { ToothStatusService } from '@app/shared/services/mstfapi/tooth-status.service';
import { PeriodontalScreeningService } from '@app/shared/services/mstfapi/periodontal-screening.service';
import { RecallOcclusionService } from '@app/shared/services/mstfapi/recall-occlusion.service';
import { RecallApplianceService } from '@app/shared/services/mstfapi/recall-appliance.service';
import { RecallTmdService } from '@app/shared/services/mstfapi/recall-tmd.service';
import { of, Subscription } from 'rxjs';
import { environment } from '@env/environment';
import { FilesService } from '@app/shared/services/api/files.service';
import { map, switchMap } from 'rxjs/operators';
import { SettingsService } from '@app/shared/services/api/settings.service';
import { AuthService } from '@app/shared/auth/auth.service';
import { FeatureFlagService } from '@app/shared/services/feature-flag.service';
import { resolveToothDisplayModel } from '@app/shared/utils/tooth-display-model.util';

@Component({
  selector: 'app-print-recall',
  templateUrl: './print-recall.component.html',
  styleUrls: ['./print-recall.component.scss']
})
export class PrintRecallComponent implements OnInit, OnDestroy {
  base_url: string = environment.apiUrlV2;

  pageTitle: string = 'New Patient Recall';
  submitted = false;
  formData: any = {};
  formPoat: any = {
    periodontal: [],
    occlusion: [],
    appliance: [],
    tmd: []
  };
  rawStatusDetail: RecallToothStatusDetail = {
    status: 0,
    color: '#FFF'
  };
  rawStatus: RecallToothStatus;
  intraOral: RecallToothStatus[] = [];
  intraOralForm: any = {
    right_0: [51, 52, 53, 54, 55],
    right_1: [11, 12, 13, 14, 15, 16, 17, 18],
    right_2: [41, 42, 43, 44, 45, 46, 47, 48],
    right_3: [81, 82, 83, 84, 85],
    left_0: [61, 62, 63, 64, 65],
    left_1: [21, 22, 23, 24, 25, 26, 27, 28],
    left_2: [31, 32, 33, 34, 35, 36, 37, 38],
    left_3: [71, 72, 73, 74, 75]
  };
  masterfile: any = {
    periodontal: [],
    occlusion: [],
    appliance: [],
    tmd: []
  };

  @Input() precall: PatientRecall;

  recall: PatientRecall;
  patient: Patient;

  printSub: Subscription;
  patientSub: Subscription;

  hasIntraOralExam: boolean = true;
  recallFiles: any[] = [];
  legendTexts: any[] = [];

  loading: boolean = true;
  private printReadySignaled = false;
  constructor(
    public printSvc: PrintService,
    public toothStatusSvc: ToothStatusService,
    private periondontalSvc: PeriodontalScreeningService,
    private occlusionSvc: RecallOcclusionService,
    private applianceSvc: RecallApplianceService,
    private tmdSvc: RecallTmdService,
    private fileService: FilesService,
    private setService: SettingsService,
    private auth: AuthService,
    private featureFlagService: FeatureFlagService
  ) {}

  ngOnInit() {
    this.toothStatusSvc.fetchToothStatuses();
    this.setBlankIntraOral();

    this.patientSub = this.printSvc.activePatient.subscribe((patient: any) => {
      this.patient = patient;
    });

    this.periondontalSvc.getAllRecord().subscribe((rows: any) => {
      this.masterfile.periodontal = rows?.data;
      this.setPoat('periodontal');
    });
    this.occlusionSvc.getAllRecord().subscribe((data: any) => {
      this.masterfile.occlusion = data.data;
      this.setPoat('occlusion');
    });
    this.applianceSvc.getAllRecords().subscribe((data) => {
      if (data && data.data) {
        this.masterfile.appliance = data.data;
        this.setPoat('appliance');
      }
    });

    this.tmdSvc.getAllRecord().subscribe((rows: any) => {
      this.masterfile.tmd = rows?.data;
      this.setPoat('tmd');
    });

    this.printSub = this.printSvc.toPrintData
      .pipe(
        switchMap((printData: any) => {
          if (!printData?.recall && !this.precall) {
            return of(printData);
          }
          const recallSys = printData?.recall?.toothNumberingSystem;
          const precallSys = this.precall?.toothNumberingSystem;
          if (recallSys || precallSys) {
            return of(printData);
          }
          const userId = this.auth.user?.data?.id;
          const settings$ = userId
            ? this.setService.getInitRecords({ associate_id: userId })
            : this.setService.getInitRecords({});
          return settings$.pipe(map(() => printData));
        })
      )
      .subscribe((printData: any) => {
        if (printData.recall) {
          this.prepareForRecallPrint();
          this.setRecall(printData.recall);

          this.recallFiles = this.getRecallAttachmentFiles(printData.recall);
          if (!this.recallFiles || this.recallFiles.length <= 0) {
            this.loading = false;
            this.markPrintReady();
          } else {
            this.loadImages();
          }
        } else if (this.precall) {
          this.prepareForRecallPrint();
          this.setRecall(this.precall);
          this.loading = false;
          this.recallFiles = this.getRecallAttachmentFiles(this.precall);
          this.markPrintReady();
        }
      });
  }

  private prepareForRecallPrint(): void {
    this.printReadySignaled = false;
    this.loading = true;
    this.hasIntraOralExam = true;
    this.legendTexts = [];
    this.formPoat = {
      periodontal: [],
      occlusion: [],
      appliance: [],
      tmd: []
    };
    this.setBlankIntraOral();
  }

  private getRecallAttachmentFiles(recall: PatientRecall): any[] {
    if (!recall || Number(recall.patientFilesId || 0) <= 0 || !Array.isArray(recall.files)) {
      return [];
    }
    return recall.files.filter((file: any) => this.isValidRecallAttachment(file));
  }

  private isValidRecallAttachment(file: any): boolean {
    return !!file && typeof file.filename === 'string' && file.filename.trim() !== '';
  }

  loadImages() {
    let totalImages: number = this.recallFiles.length;
    this.loading = true;

    if (!totalImages) {
      this.loading = false;
      this.markPrintReady();
      return;
    }

    this.recallFiles.forEach((file: any) => {
      const img = new Image();
      img.onload = (e) => {
        totalImages -= 1;
        if (totalImages <= 0) {
          this.loading = false;
          this.markPrintReady();
        }
      };
      img.onerror = (e) => {
        totalImages -= 1;
        if (totalImages <= 0) {
          this.loading = false;
          this.markPrintReady();
        }
      };
      img.src = this.base_url + '/file?f=' + file.filename;
    });
  }

  private markPrintReady(): void {
    if (this.printReadySignaled) {
      return;
    }
    this.printReadySignaled = true;
    setTimeout(() => {
      this.printSvc.contentReady = true;
    }, 100);
  }

  ngOnDestroy() {
    this.printSub.unsubscribe();
    this.patientSub.unsubscribe();
  }

  setRecall(recall: PatientRecall) {
    this.recall = recall;
    let intraOral = this.parseIntraOralExam(this.recall.intraoralExam);

    if (!intraOral || intraOral.length == 0) {
      this.hasIntraOralExam = false;
      this.setPoat();
      return;
    }
    intraOral.forEach((row) => {
      if (!row?.data) {
        return;
      }
      if (row.data.legendsObj && row.data.legendsObj.length > 0) {
        row.data.legendsObj.forEach((obj) => {
          if (obj.cd && obj.t) {
            this.legendTexts.push({ c: obj.cd, t: obj.t });
          }
        });
      }
      row.data.touched = true;
      this.intraOral[row.pos] = row.data;
    });
    this.setPoat();
  }

  private parseIntraOralExam(intraoralExam: any): any[] {
    if (!intraoralExam) {
      return [];
    }
    if (Array.isArray(intraoralExam)) {
      return intraoralExam;
    }
    try {
      const parsed = JSON.parse(intraoralExam);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  setPoat(key?: string) {
    if (!this.recall || !this.recall.id) {
      return;
    }
    let rkey = key;
    if (key == 'periodontal') {
      rkey = 'periodontalScreening';
    }
    if (key) {
      // Rebuild this section from scratch so re-running setPoat (e.g. when a
      // masterfile finishes loading after the recall is already set) refreshes
      // the values instead of appending stale/duplicate entries.
      this.formPoat[key] = [];
      let records = this.getRecallCsvValues(rkey);
      if (this.masterfile[key] && this.masterfile[key].length > 0) {
        this.masterfile[key].forEach((row) => {
          if (row.id && records.indexOf(row.id.toString()) >= 0) {
            this.formPoat[key].push(row.name);
          }
        });
      }
    } else {
      let keys = ['periodontal', 'occlusion', 'appliance', 'tmd'];
      keys.forEach((key) => {
        this.setPoat(key);
      });
    }
  }

  private getRecallCsvValues(key: string): string[] {
    const value = this.recall ? this.recall[key] : '';
    if (Array.isArray(value)) {
      return value.map((item) => item?.toString()).filter((item) => !!item);
    }
    return (value || '')
      .toString()
      .split(',')
      .map((item) => item.trim())
      .filter((item) => item !== '');
  }

  setBlankIntraOral() {
    Object.keys(this.intraOralForm).forEach((key) => {
      this.intraOralForm[key].forEach((idx) => {
        let rawStatus: RecallToothStatus = {
          cen: this.copy(this.rawStatusDetail),
          top: this.copy(this.rawStatusDetail),
          lef: this.copy(this.rawStatusDetail),
          rig: this.copy(this.rawStatusDetail),
          bot: this.copy(this.rawStatusDetail),
          legendsStr: [],
          legendsObj: [],
          legends: {}
        };
        this.intraOral[idx] = rawStatus; //this.copy(rawStatus);
      });
    });
  }

  getToothModelLabel(status: RecallToothStatus): string {
    if (!this.featureFlagService.useTwoDimensionalChartingAction()) {
      return '';
    }
    const model = resolveToothDisplayModel(status);
    return model.key === 'natural' ? '' : model.label;
  }

  useToothDisplayModels(): boolean {
    return this.featureFlagService.useTwoDimensionalChartingAction();
  }

  getToothModelKey(status: RecallToothStatus): string {
    return this.useToothDisplayModels() ? resolveToothDisplayModel(status).key : 'natural';
  }

  isToothModelSurfaceCompatible(status: RecallToothStatus): boolean {
    return !this.useToothDisplayModels() || resolveToothDisplayModel(status).surfaceCompatible;
  }

  copy(source) {
    return JSON.parse(JSON.stringify(source));
  }
}
